import { all, call, put, takeEvery, select } from "redux-saga/effects";
import { normalize, schema } from "normalizr";
import { getErrorMessage } from "utils/sagasErrorHandler";
import { FiltersFieldsTypes } from "modules/SelectEntityByType/constants";
import { getFiltersOptions, getFiltersOptionsError, getFiltersOptionsSuccess } from "modules/SelectEntityByType/state/actions";
import {
    getBlockSelectorSourceRequest,
    getBlockSelectorTypeRequest,
    getBranchRequest,
    getBrandRequest,
    getCountriesRequest,
    getDataSelectorActionRequest,
    getTemplateScreenshotActionRequest,
    getDataSelectorTypeRequest,
    getNormalizerRequest,
    getProxyTypeRequest,
    getReceiverRequest,
    getResponseProviderRequest,
    getRetryRuleActionRequest,
    getRetryRuleTypeRequest,
    getRolesRequest,
    getSourceRequest,
    getStatusRequest,
    getStatusTypeRequest,
    getTemplateSelectorConditionRequest,
    getTemplateSelectorSourceRequest,
    getTemplateSelectorTypeRequest,
    getTenantsRequest,
    getUserAgentsRequest,
    getValidatorsObjectPropertiesRequest,
    getObjectStructuresRequest,
    getConditionTypeRequest,
    getPublishRequestStatusesRequest,
    getUsersRequest,
    getConfigCiTestTaskProgressesRequest,
    getHttpMethodFieldRequest,
    getMetricTaskExecutorsRequest,
    getMetricTaskTemplatesRequest,
    getCredentialsTypesRequest,
    getStorageTypesRequest,
    getProxyTagsRequest,
    branchTagsRequest,
    searchUrlPatternTypesRequest,
    searchUrlPatternSortingsRequest,
    primaryKeyItemTypesRequest,
    quotaTypesRequest,
    getSiteGroupsRequest,
    getPlatformsRequest,
    getBrightDataScrapingBrowserCredentialsRequest,
    getOxylabsScrapingBrowserCredentialsRequest,
} from "modules/SelectEntityByType/state/api";
import {getPlatformIDsRequest} from "modules/PrimaryKey/services/PrimaryKey/api";
import { entitySelector } from "modules/SelectEntityByType/state/selectors";

const objectStructuresSchema = new schema.Entity("properties" , {},{ idAttribute: "code" });
const objectStructuresListSchema = new schema.Array(objectStructuresSchema);

function* handleGetFilterDataByType(filterType, params) {
    switch (filterType) {
        case FiltersFieldsTypes.countries: {
            const { countries } = yield call(getCountriesRequest);

            return { result: (countries && countries.map(({ iso_code2, name }) => ({ value: iso_code2, label: name }))) || [] };
        }
        case FiltersFieldsTypes.siteGroups: {
            const { site_groups } = yield call(getSiteGroupsRequest);

            return { result: (site_groups && site_groups.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.roles: {
            const { roles } = yield call(getRolesRequest);

            return { result: (roles && roles.map(({ id, role }) => ({ value: String(id), label: role }))) || [] };
        }
        case FiltersFieldsTypes.tenants: {
            const { tenants } = yield call(getTenantsRequest);

            return { result: (tenants && tenants.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.receiver: {
            const { receivers } = yield call(getReceiverRequest);

            return { result: (receivers && receivers.map(({ code, name, inform_in_any_case }) => ({ value: code, label: name, inform_in_any_case: inform_in_any_case }))) || [] };
        }
        case FiltersFieldsTypes.brand: {
            const { brands } = yield call(getBrandRequest);
            const result = (brands && brands.map(({ code, name, metadata }) => ({
                value: code,
                label: name,
                scope: metadata?.metadata_property
                    ?.find(({ key }) => key === "SCOPE")
                    ?.values?.[0].toLowerCase() || ""
            }))) || [];
            return { result: result };
        }
        case FiltersFieldsTypes.branch: {
            const { branches } = yield call(getBranchRequest);

            return { result: (branches && branches.map(({ code, name, site_group_code, auto_enable_keyword }) => ({
                value: code, label: name, site_group_code: site_group_code, auto_enable_keyword: auto_enable_keyword
            }))) || [] };
        }
        case FiltersFieldsTypes.source: {
            const { sources } = yield call(getSourceRequest);

            return { result: (sources && sources.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.primaryKeyPlatformIDs: {
            const { platform_ids } = yield call(getPlatformIDsRequest);
            return { result: (platform_ids && platform_ids.map((platform_id) => ({ value: platform_id, label: platform_id }))) || [] };
        }
        case FiltersFieldsTypes.statusType: {
            const { status_types } = yield call(getStatusTypeRequest);

            return { result: (status_types && status_types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.status: {
            const { statuses } = yield call(getStatusRequest);

            return { result: (statuses && statuses.map(({ code, name, status_type_code }) => ({ value: code, label: name, type: status_type_code }))) || [] };
        }
        case FiltersFieldsTypes.validatorsObjectProperties: {
            const { validators } = yield call(getValidatorsObjectPropertiesRequest);

            return { result: (validators && validators.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.userAgentName: {
            const { user_agents } = yield call(getUserAgentsRequest);

            return { result: (user_agents && user_agents.map(({ name, tls_client_profile }) => ({ value: name, label: tls_client_profile ? `(${tls_client_profile}) ${name}` : name, tls_client_profile: tls_client_profile }))) || [] };
        }
        case FiltersFieldsTypes.responseProvider: {
            const { providers } = yield call(getResponseProviderRequest);

            return { result: (providers && providers.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.proxyType: {
            const { proxy_types } = yield call(getProxyTypeRequest);

            return { result: (proxy_types && proxy_types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.proxyProtocols: {
            const proxyProtocols = [
                {
                    value: "HTTP",
                    label: "HTTP",
                },
                {
                    value: "SOCKS5",
                    label: "SOCKS5",
                },
            ];

            return { result: proxyProtocols };
        }
        case FiltersFieldsTypes.zealCatalogs: {
            const zealCatalogs = [
                {
                    value: "46108",
                    label: "Monster Summer",
                },
            ];

            return { result: zealCatalogs };
        }
        case FiltersFieldsTypes.retryRuleType: {
            const { retry_rule_types } = yield call(getRetryRuleTypeRequest);

            return { result: (retry_rule_types && retry_rule_types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.retryRuleAction: {
            const { retry_rule_actions } = yield call(getRetryRuleActionRequest);

            return { result: (retry_rule_actions && retry_rule_actions.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.templateSelectorSource: {
            const { sources } = yield call(getTemplateSelectorSourceRequest);

            return { result: (sources && sources.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.templateSelectorCondition: {
            const { conditions } = yield call(getTemplateSelectorConditionRequest);

            return { result: (conditions && conditions.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.templateSelectorType: {
            const { types } = yield call(getTemplateSelectorTypeRequest);

            return { result: (types && types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.blockSelectorSource: {
            const { sources } = yield call(getBlockSelectorSourceRequest);

            return { result: (sources && sources.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.blockSelectorType: {
            const { types } = yield call(getBlockSelectorTypeRequest);

            return { result: (types && types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.dataSelectorType: {
            const { types } = yield call(getDataSelectorTypeRequest);

            return { result: (types && types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.dataSelectorAction: {
            const { actions } = yield call(getDataSelectorActionRequest);

            return { result: (actions && actions.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.templateScreenshotAction: {
            const { template_screenshot_actions } = yield call(getTemplateScreenshotActionRequest);

            return { result: (template_screenshot_actions && template_screenshot_actions.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.normalizer: {
            const { normalizers } = yield call(getNormalizerRequest);

            return { result: (normalizers && normalizers.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.dataSelectorConditionType: {
            const { types } = yield call(getConditionTypeRequest);

            return { result: (types && types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.objectStructures: {
            const { object_structures } = yield call(getObjectStructuresRequest, params);
            const { entities } = normalize(object_structures, objectStructuresListSchema);

            return {
                result: (object_structures && object_structures.map(({ code, name }) => ({ value: code, label: name }))) || [],
                properties: entities.properties || []
            };
        }
        case FiltersFieldsTypes.publishRequestStatuses: {
            const { publish_request_statuses } = yield call(getPublishRequestStatusesRequest);

            return { result: (publish_request_statuses && publish_request_statuses.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.browserSourceTypes: {
            const browserSourceTypes = [
                {
                    value: "_",
                    label: "Default: SF (Selenium | Net HTTP)",
                    renderJS: true,
                    common: true,
                },
                {
                    value: "BRIGHTDATA",
                    label: "Brightdata (Chrome)",
                    renderJS: true,
                    common: false,
                },
                {
                    value: "OXYLABS",
                    label: "Oxylabs (Chrome)",
                    renderJS: true,
                    common: false,
                },
                {
                    value: "CAMOUFOX",
                    label: "Camoufox (Firefox)",
                    renderJS: true,
                    common: false,
                },
                {
                    value: "SF_REMOTE",
                    label: "Selenium (Chrome)",
                    renderJS: true,
                    common: false,
                },
                {
                    value: "CLOAK",
                    label: "Cloak (Chrome)",
                    renderJS: true,
                    common: false,
                },
                {
                    value: "CLOAK_LATEST",
                    label: "Cloak latest (Chrome)",
                    renderJS: true,
                    common: false,
                },
                {
                    value: "NODRIVER",
                    label: "Nodriver (Chrome)",
                    renderJS: true,
                    common: false,
                },
                {
                    value: "NET_HTTP",
                    label: "Net HTTP",
                    renderJS: false,
                    common: false,
                },
                {
                    value: "TLS_CLIENT",
                    label: "TLS Client",
                    renderJS: false,
                    common: false,
                },
            ];

            return { result: browserSourceTypes };
        }
        case FiltersFieldsTypes.userAgentEngines: {
            const userAgentEngines = [
                {
                    value: "Chrome",
                    label: "Chrome",
                },
                {
                    value: "Firefox",
                    label: "Firefox",
                },
                {
                    value: "Edge",
                    label: "Edge",
                },
                {
                    value: "Opera",
                    label: "Opera",
                },
                {
                    value: "Safari",
                    label: "Safari",
                },
                {
                    value: "Vivaldi",
                    label: "Vivaldi",
                },
                {
                    value: "Other",
                    label: "Other",
                },
            ];

            return { result: userAgentEngines };
        }
        case FiltersFieldsTypes.queueDriverTypes: {
            const queueDriverTypes = [
                {
                    value: "_",
                    label: "Default: Simple Queue Service",
                },
                {
                    value: "SQS",
                    label: "Simple Queue Service",
                },
            ];

            return { result: queueDriverTypes };
        }
        case FiltersFieldsTypes.fulfillmentSystems: {
            const fulfillmentSystems = [
                {
                    value: "SCRAPERFORCE",
                    label: "ScraperForce",
                },
                {
                    value: "CTQ",
                    label: "Cyborg Task Queue",
                },
                {
                    value: "DW",
                    label: "DW",
                },
            ];

            return { result: fulfillmentSystems };
        }
        case FiltersFieldsTypes.users: {
            const { users } = yield call(getUsersRequest, {"is_deleted": false});
            const { platforms } = yield call(getPlatformsRequest);
            const platformsMap = {};
            if (platforms && platforms.length > 0) {
                platforms.forEach(platform => {
                    platformsMap[platform.code] = platform.name;
                })
            }

            return { result: (users && users.map(({ id, first_name, last_name, platform_code }) => ({ value: String(id), label: `${first_name} ${last_name} (${platformsMap[platform_code] ? platformsMap[platform_code] : ""})` }))) || [] };
        }
        case FiltersFieldsTypes.configCiTestTaskProgresses: {
            const { config_ci_test_progresses } = yield call(getConfigCiTestTaskProgressesRequest);

            return { result: (config_ci_test_progresses && config_ci_test_progresses.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.httpMethodField: {
            const { http_methods } = yield call(getHttpMethodFieldRequest);

            return { result: (http_methods && http_methods.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.platforms: {
            const { platforms } = yield call(getPlatformsRequest);

            return { result: (platforms && platforms.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.metricTaskExecutorsField: {
            const { metric_task_executors } = yield call(getMetricTaskExecutorsRequest);

            return { result: (metric_task_executors && metric_task_executors.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.metricTaskTemplatesField: {
            const { metric_task_templates } = yield call(getMetricTaskTemplatesRequest);

            return { result: (metric_task_templates && metric_task_templates.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.credentialsTypes: {
            const { credentials_types } = yield call(getCredentialsTypesRequest);

            return { result: (credentials_types && credentials_types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.brightDataScrapingBrowserCredentials: {
            const { credentials } = yield call(getBrightDataScrapingBrowserCredentialsRequest);

            return { result: (credentials && credentials.map(({ id, data }) => ({ value: id, label: data?.name }))) || [] };
        }
        case FiltersFieldsTypes.oxylabsScrapingBrowserCredentials: {
            const { credentials } = yield call(getOxylabsScrapingBrowserCredentialsRequest);

            return { result: (credentials && credentials.map(({ id, data }) => ({ value: id, label: data?.name }))) || [] };
        }
        case FiltersFieldsTypes.storage: {
            const { storage_types } = yield call(getStorageTypesRequest);

            return { result: (storage_types && storage_types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.proxyTags: {
            const tags = yield call(getProxyTagsRequest);

            return { result: (tags && tags.map((tagName) => ({ value: tagName, label: tagName }))) || [] };
        }
        case FiltersFieldsTypes.branchTags: {
            const { tags } = yield call(branchTagsRequest);

            return { result: (tags && tags.map((tagName) => ({ value: tagName, label: tagName }))) || [] };
        }
        case FiltersFieldsTypes.searchUrlPatternTypes: {
            const { search_url_pattern_types } = yield call(searchUrlPatternTypesRequest);

            return { result: (search_url_pattern_types && search_url_pattern_types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.primaryKeyItemTypes: {
            const { item_types } = yield call(primaryKeyItemTypesRequest);

            return { result: (item_types && item_types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.searchUrlPatternSortings: {
            const { search_url_pattern_sortings } = yield call(searchUrlPatternSortingsRequest);

            return { result: (search_url_pattern_sortings && search_url_pattern_sortings.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }
        case FiltersFieldsTypes.quotaTypes: {
            const { types } = yield call(quotaTypesRequest);

            return { result: (types && types.map(({ code, name }) => ({ value: code, label: name }))) || [] };
        }

        default:
            if (process.env.NODE_ENV === "production") {
                return [];
            }
            throw new Error(`Oops! GET of the FilterType - ${filterType} was not implemented.`);
    }
}

function* handleGetFilterData({ payload: { filterType, params } }) {
    try {
        const filters = yield select(entitySelector);
        if(filters[filterType]?.options) {
            return;
        }

        const { result, ...restProps } = yield call(handleGetFilterDataByType, filterType, params);

        yield put(getFiltersOptionsSuccess({ options: result, filterType, ...restProps }));
    } catch (error) {
        yield put(getFiltersOptionsError({ filterType }));
        getErrorMessage(error);
    }
}

export function* entityByTypeSaga() {
    yield all([
        yield takeEvery(
            getFiltersOptions,
            handleGetFilterData
        )
    ]);
}
